import React, { useRef, useState } from 'react';
import { OverlayTrigger, Popover, Table } from 'react-bootstrap';

import { formatMoney, formatNomenclatureName, formatNumber, formatUnit } from '../../utils/gazTextFormat.js';
import { buildGazTotalBreakdown } from './gazTotalBreakdown.js';

/**
 * Общая сумма строки сравнения с расшифровкой — МСК Газобетон.
 *
 * Подсказка собрана на тех же react-bootstrap OverlayTrigger и Popover, что и
 * старые MaterialsTooltip и DeliveryTooltip: своего механизма всплывания,
 * позиционирования и закрытия не заводится.
 *
 * Триггеры: наведение и фокус — как у подсказки доставки; нажатие — как у
 * подсказки материалов. На телефоне наведения нет, поэтому расшифровка
 * открывается нажатием, а закрывается повторным нажатием (сам OverlayTrigger
 * переключает состояние) или нажатием снаружи (rootClose).
 *
 * Своих чисел не считает: всё приходит из buildGazTotalBreakdown, который читает
 * уже посчитанные поля строки.
 */

const valueOrDash = (value, render) => (
  value === null || value === undefined ? '—' : render(value)
);

const GazTotalTooltip = ({ entry, payableTotal, children }) => {
  // Состоянием управляем сами — как MaterialsTooltip. Иначе нажатие спорит с
  // наведением: браузер на касании шлёт ещё и совместимостные mouseover с focus,
  // и окно открывалось обратно сразу после закрытия.
  const [show, setShow] = useState(false);
  // Чем именно пользуются прямо сейчас. Мышь — по умолчанию: наведение бывает
  // и без единого нажатия.
  const pointerTypeRef = useRef('mouse');
  const isTouch = () => pointerTypeRef.current === 'touch';
  // Было ли окно открыто до касания. Снимается в pointerdown — раньше, чем
  // rootClose успевает закрыть окно нажатием по самой сумме, иначе следующий
  // за ним обработчик нажатия открывал бы его обратно.
  const wasOpenRef = useRef(false);

  // Чем пользуются, запоминаем и при наведении: иначе одно касание навсегда
  // переводило бы подсказку в «телефонный» режим, и мышью она больше не
  // открывалась бы наведением.
  const rememberPointer = (event) => {
    pointerTypeRef.current = event.pointerType || 'mouse';
  };

  const handlePointerDown = (event) => {
    rememberPointer(event);
    wasOpenRef.current = show;
  };

  // Наведение и фокус открывают окно только мышью и с клавиатуры. Закрытие
  // пропускаем всегда: им пользуется rootClose, когда нажали снаружи.
  const handleToggle = (next) => {
    if (next && isTouch()) {
      return;
    }
    setShow(next);
  };

  const handleClick = (event) => {
    // Выбор производства при этом не меняется: строка вокруг — тоже орган выбора.
    event.stopPropagation();
    // Мышью окном управляет наведение — нажатие ничего не переключает.
    if (!isTouch()) {
      return;
    }
    setShow(!wasOpenRef.current);
  };

  const breakdown = buildGazTotalBreakdown({ entry, payableTotal });

  // Расшифровывать нечего — показываем сумму как обычный текст.
  if (!breakdown || (breakdown.materials.length === 0 && breakdown.delivery.length === 0)) {
    return children;
  }

  const popover = (
    <Popover
      id="gaz-total-popover"
      className="gaz-cmp__popover"
      style={{ maxWidth: 'min(92vw, 720px)', maxHeight: '70vh', overflow: 'auto' }}
    >
      <Popover.Header as="h5">Из чего складывается сумма</Popover.Header>
      <Popover.Body>
        <div className="gaz-cmp__popover-section">Материалы</div>
        {/* Таблица шире узкого экрана не растягивает окно, а прокручивается
            внутри него — тот же приём, что в подсказке доставки. */}
        <div className="gaz-cmp__popover-scroll">
        <Table striped bordered hover size="sm" style={{ width: '100%', tableLayout: 'auto' }}>
          <thead>
            <tr>
              <th>Название</th>
              <th>Кол-во</th>
              <th>Цена</th>
              <th>Сумма</th>
            </tr>
          </thead>
          <tbody>
            {breakdown.materials.map((material, index) => (
              <tr key={`${material.name}-${index}`}>
                <td style={{ wordBreak: 'break-word' }}>{formatNomenclatureName(material.name)}</td>
                <td>
                  {valueOrDash(material.quantity, (value) => (
                    `${formatNumber(value)}${material.unit ? ` ${formatUnit(material.unit)}` : ''}`
                  ))}
                </td>
                <td>{valueOrDash(material.price, (value) => `${formatMoney(value)} ₽`)}</td>
                <td>{valueOrDash(material.sum, (value) => `${formatMoney(value)} ₽`)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        </div>

        {breakdown.materialsTotal !== null && (
          <div className="gaz-cmp__popover-subtotal">
            Итого материалы: <b>{formatMoney(breakdown.materialsTotal)} ₽</b>
          </div>
        )}

        {breakdown.hasDeliveryError ? (
          <div className="gaz-cmp__popover-error">Не удалось рассчитать доставку</div>
        ) : breakdown.delivery.length > 0 && (
          <>
            <div className="gaz-cmp__popover-section">Доставка</div>
            <div className="gaz-cmp__popover-scroll">
            <Table striped bordered hover size="sm" style={{ width: '100%', tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th>Транспорт</th>
                  <th>Рейсов</th>
                  <th>Цена рейса</th>
                  <th>Сумма</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.delivery.map((line, index) => (
                  <tr key={`${line.category}-${index}`}>
                    <td style={{ wordBreak: 'break-word' }}>{line.transport}</td>
                    <td>{valueOrDash(line.trips, (value) => formatNumber(value))}</td>
                    <td>{valueOrDash(line.costPerTrip, (value) => `${formatMoney(value)} ₽`)}</td>
                    <td>{valueOrDash(line.sum, (value) => `${formatMoney(value)} ₽`)}</td>
                  </tr>
                ))}

                {/* Разгрузка отдельной строкой — но она уже сидит внутри суммы
                    доставки, поэтому подпись прямо об этом говорит. */}
                {breakdown.unloading && (
                  <tr className="gaz-cmp__popover-unloading">
                    <td colSpan={3} style={{ wordBreak: 'break-word' }}>
                      Разгрузка: {breakdown.unloading.method} (входит в сумму доставки)
                    </td>
                    <td>{formatMoney(breakdown.unloading.sum)} ₽</td>
                  </tr>
                )}
              </tbody>
            </Table>
            </div>

            {breakdown.deliveryTotal !== null && (
              <div className="gaz-cmp__popover-subtotal">
                Итого доставка: <b>{formatMoney(breakdown.deliveryTotal)} ₽</b>
              </div>
            )}
          </>
        )}

        {breakdown.total !== null && (
          <div className="gaz-cmp__popover-total">
            Итого к оплате: <b>{formatMoney(breakdown.total)} ₽</b>
          </div>
        )}
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      // Наведение и фокус — как у подсказки доставки. Нажатие обрабатываем сами:
      // встроенный click-триггер вместе с rootClose переоткрывал окно вместо
      // того, чтобы закрыть его повторным нажатием.
      trigger={['hover', 'focus']}
      show={show}
      onToggle={handleToggle}
      // На узком экране слева места нет — окно само выбирает сторону.
      placement="auto"
      flip
      rootClose
      overlay={popover}
      delay={{ show: 120, hide: 100 }}
      popperConfig={{
        modifiers: [
          { name: 'preventOverflow', options: { boundary: 'viewport' } },
          { name: 'offset', options: { offset: [0, 10] } },
        ],
      }}
    >
      <span
        className="gaz-cmp__total-value"
        tabIndex={0}
        role="button"
        aria-label="Показать расшифровку суммы"
        onPointerEnter={rememberPointer}
        onPointerDown={handlePointerDown}
        onClick={handleClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.stopPropagation();
          }
        }}
      >
        {children}
      </span>
    </OverlayTrigger>
  );
};

export default GazTotalTooltip;
